import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { StaleChunkBoundary } from './components/StaleChunkBoundary';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/query-client';
import './index.css';

// Layouts (eagerly loaded — always needed)
import RootLayout from './routes/_layout';
import AuthLayout from './routes/auth/_layout';

// Feed page lazy-loaded — behind auth guard, Suspense covers the gap
const LandingPage = lazy(() => import('./routes/landing'));
const FeedPage = lazy(() => import('./routes/index'));

// Code-split routes
const PostPage = lazy(() => import('./routes/post.$id'));
const GaragePage = lazy(() => import('./routes/garage.$slug'));
const GaragesPage = lazy(() => import('./routes/garages'));
const CreateGaragePage = lazy(() => import('./routes/garages.new'));
const GarageProfilePage = lazy(() => import('./routes/garage-profile.$username'));
const UserPage = lazy(() => import('./routes/user.$username'));
const UserFollowersPage = lazy(() => import('./routes/user.$username.followers'));
const UserPicks2026Page = lazy(() => import('./routes/user.$username.picks-2026'));
const UserFollowingPage = lazy(() => import('./routes/user.$username.following'));
const BookmarksPage = lazy(() => import('./routes/bookmarks'));
const InvitePage = lazy(() => import('./routes/invite'));
const SettingsPage = lazy(() => import('./routes/settings'));
const NotificationsPage = lazy(() => import('./routes/notifications'));
const SearchPage = lazy(() => import('./routes/search'));
const ExplorePage = lazy(() => import('./routes/explore'));
const SourcePage = lazy(() => import('./routes/source.$id'));
const SourcesPage = lazy(() => import('./routes/sources'));
const ScootersPage = lazy(() => import('./routes/scooters'));
const LaunchesPage = lazy(() => import('./routes/launches'));
const LaunchCalendarPage = lazy(() => import('./routes/launches.upcoming'));
const Triumph350Page = lazy(() => import('./routes/bikes.triumph-350'));
const ToyotaEbellaPage = lazy(() => import('./routes/cars.toyota.urban-cruiser-ebella'));
const MercedesClaElectricPage = lazy(() => import('./routes/cars.mercedes.cla-electric-2026'));
const NissanTektonPage = lazy(() => import('./routes/cars.nissan.tekton'));
const TataSierraEvPage = lazy(() => import('./routes/cars.tata.sierra-ev'));
const SierraEvLaunchLivePage = lazy(() => import('./routes/cars.tata.sierra-ev-launch-live'));
const Bs7EmissionNormsPage = lazy(() => import('./routes/guides.bs7-emission-norms-india-2026'));
const BestEvUnder20LPage = lazy(() => import('./routes/guides.best-ev-under-20-lakh-india-2026'));
const IndiaEvTwoWheelerMarketSharePage = lazy(() => import('./routes/ev.india-ev-two-wheeler-market-share'));
const IndiaEvFy2026Page = lazy(() => import('./routes/data.india-ev-fy2026'));
const IndiaEvAnnualReportFy2026Page = lazy(() => import('./routes/data.india-ev-annual-report-fy2026'));
const India2wFy2026Page = lazy(() => import('./routes/data.india-2w-fy2026'));
const IndiaCarSalesPage = lazy(() => import('./routes/data.india-car-sales'));
const IndiaCarFY2026Top10Page = lazy(() => import('./routes/data.india-car-fy2026-top10'));
const Gst350ccImpactPage = lazy(() => import('./routes/bikes.gst-350cc-impact'));
const MajestorVsFortunerPage = lazy(() => import('./routes/cars.mg.majestor-vs-fortuner-2026'));
const BsaScrambler650VsBear650Page = lazy(() => import('./routes/bikes.bsa.scrambler-650-vs-re-bear-650'));
const IndiaEvPvFy2026Page = lazy(() => import('./routes/data.india-ev-pv-fy2026'));
const MercedesBenzClaElectricPage = lazy(() => import('./routes/cars.mercedes-benz.cla-electric'));
const Himalayan750Page = lazy(() => import('./routes/bikes.royal-enfield.himalayan-750'));
const BsaScrambler650Page = lazy(() => import('./routes/bikes.bsa.scrambler-650'));
const EbellaVsEVitaraPage = lazy(() => import('./routes/cars.toyota.ebella-vs-maruti-e-vitara'));
const GstCalculatorPage = lazy(() => import('./routes/bikes.gst-calculator'));
const DelhiEvPolicyCalculatorPage = lazy(() => import('./routes/guides.delhi-ev-policy-calculator'));
const MotoGpIndia2026Page = lazy(() => import('./routes/guides.motogp-india-2026'));
const Cafe3PriceImpactCalculatorPage = lazy(() => import('./routes/guides.cafe3-price-impact-calculator'));
const IndiaMarch2026TwoWheelerSalesPage = lazy(() => import('./routes/data.india-march-2026-two-wheeler-sales'));
const MotoGp2026StandingsPage = lazy(() => import('./routes/guides.motogp-2026-standings'));
const EVitaraBaaSCalculatorPage = lazy(() => import('./routes/guides.evitara-baas-calculator'));
const EvSubsidyCountdownPage = lazy(() => import('./routes/guides.ev-subsidy-countdown'));
const EvSubsidyCalculatorPage = lazy(() => import('./routes/tools.ev-subsidy-calculator'));
const EmiCalculatorPage = lazy(() => import('./routes/tools.emi-calculator'));
const EVInsuranceCalculatorPage = lazy(() => import('./routes/tools.ev-insurance-calculator'));
const MercedesClaElectricIndiaLaunchPage = lazy(() => import('./routes/cars.mercedes-benz.cla-electric-india-launch'));
const NissanTektonIndiaLaunchPage = lazy(() => import('./routes/cars.nissan.tekton-india-launch'));
const IndiaCarSalesMarch2026Page = lazy(() => import('./routes/data.india-car-sales-march-2026'));
const EbellaVariantGuidePage = lazy(() => import('./routes/cars.toyota.ebella-variant-guide'));
const MgMajestorIndiaLaunchPage = lazy(() => import('./routes/cars.mg.majestor-india-launch'));
const BestScootersUnder1Lakh2026Page = lazy(() => import('./routes/guides.best-scooters-under-1-lakh-2026'));
const EbellaIndiaLaunchPage = lazy(() => import('./routes/cars.toyota.ebella-india-launch'));
const BestCarDiscountsApril2026Page = lazy(() => import('./routes/guides.best-car-discounts-april-2026'));
const BharatNcap5StarCars2026Page = lazy(() => import('./routes/guides.bharat-ncap-5-star-cars-2026'));
const IndiaCarSalesFy2026AnalysisPage = lazy(() => import('./routes/data.india-car-sales-fy2026-analysis'));
const DelhiEvPolicy2026TaxExemptionsPage = lazy(() => import('./routes/guides.delhi-ev-policy-2026-tax-exemptions'));
const GstHike350ccBikesPage = lazy(() => import('./routes/guides.gst-hike-350cc-bikes-india-2026'));
const BsaScrambler650IndiaLaunchPage = lazy(() => import('./routes/bikes.bsa.scrambler-650-india-launch'));
const Himalayan750IndiaLaunchPage = lazy(() => import('./routes/bikes.royal-enfield.himalayan-750-india-launch'));
const IndiaEvQ1_2026SalesPage = lazy(() => import('./routes/data.india-ev-q1-2026-sales'));
const BeijingAutoShow2026IndiaPage = lazy(() => import('./routes/guides.beijing-auto-show-2026-india'));
const F1MiamiGp2026PreviewPage = lazy(() => import('./routes/guides.f1-miami-gp-2026-preview'));
const ChineseEvsIndia2026BuyersGuidePage = lazy(() => import('./routes/guides.chinese-evs-india-2026-buyers-guide'));
const F12026SeasonGuidePage = lazy(() => import('./routes/guides.f1-2026-season-guide'));
const DelhiEvPolicy20262030Page = lazy(() => import('./routes/guides.delhi-ev-policy-2026-2030'));
const KimiAntonelliF12026ChampionshipPage = lazy(() => import('./routes/guides.kimi-antonelli-f1-2026-championship'));
const GlobalEvQ1_2026SalesPage = lazy(() => import('./routes/data.global-ev-q1-2026-sales'));
const IndiaPvSalesMarch2026Page = lazy(() => import('./routes/data.india-pv-sales-march-2026'));
const F12026SuperClippingRulePage = lazy(() => import('./routes/guides.f1-2026-super-clipping-rule'));
const MotoGpSpanishGpJerez2026Page = lazy(() => import('./routes/guides.motogp-spanish-gp-jerez-2026'));
const MgMajestorBuyersGuide2026Page = lazy(() => import('./routes/guides.mg-majestor-buyers-guide-2026'));
const IndiaEvChargingInfrastructure2026Page = lazy(() => import('./routes/guides.india-ev-charging-infrastructure-2026'));
const EvChargingHighwaysIndia2026Page = lazy(() => import('./routes/guides.ev-charging-highways-india-2026'));
const F1MonacoGp2026PreviewPage = lazy(() => import('./routes/guides.f1-2026-monaco-gp-preview'));
const India2wFy2026SalesAnalysisPage = lazy(() => import('./routes/guides.india-2w-fy2026-sales-analysis'));
const EbellaBaasBuyersGuide2026Page = lazy(() => import('./routes/cars.toyota.ebella-baas-buyers-guide-2026'));
const IndiaBikeLaunchesApril2026Page = lazy(() => import('./routes/guides.india-bike-launches-april-2026'));
const WrcCroatiaRally2026Page = lazy(() => import('./routes/guides.wrc-2026-croatia-rally'));
const MotoGp2026MidSeasonAnalysisPage = lazy(() => import('./routes/guides.motogp-2026-mid-season-analysis'));
const DelhiEvMandate20262030Page = lazy(() => import('./routes/guides.delhi-ev-mandate-2026-2030'));
const F12026RegulationCrisisPage = lazy(() => import('./routes/guides.f1-2026-regulation-crisis'));
const April23TripleBikeLaunchPage = lazy(() => import('./routes/guides.april-23-triple-bike-launch-india-2026'));
const FlyingFleaC6BaasVsOwnershipPage = lazy(() => import('./routes/guides.flying-flea-c6-baas-vs-ownership'));
const IndianBikeWeek2026Page = lazy(() => import('./routes/events.indian-bike-week-2026'));
const BudgetDiscoveryPage = lazy(() => import('./routes/find.$slug'));
const IndiaAutoSalesPage = lazy(() => import('./routes/market.india-auto-sales'));
const ExtensionWaitlistPage = lazy(() => import('./routes/extension'));
const PrivacyPage = lazy(() => import('./routes/privacy'));
const TermsPage = lazy(() => import('./routes/terms'));
const AboutPage = lazy(() => import('./routes/about'));
const ContactPage = lazy(() => import('./routes/contact'));
const LeaderboardPage = lazy(() => import('./routes/leaderboard'));
const DigestPage = lazy(() => import('./routes/digest'));
const DispatchPage = lazy(() => import('./routes/dispatch'));
const StatusPage = lazy(() => import('./routes/status'));
const ProPage = lazy(() => import('./routes/pro'));
const HistoryPage = lazy(() => import('./routes/history'));
const TagsPage = lazy(() => import('./routes/tags'));
const TagPage = lazy(() => import('./routes/tag.$slug'));
const AiQualityDashboard = lazy(() => import('./routes/runtime/ai-quality'));
const ShareBreakdownDashboard = lazy(() => import('./routes/runtime/share-breakdown'));
const AffiliateRevenueDashboard = lazy(() => import('./routes/runtime/affiliate-revenue'));
const ProFunnelDashboard = lazy(() => import('./routes/runtime/pro-funnel'));
const AIComparePage = lazy(() => import('./routes/compare'));
const BrandHubPage = lazy(() => import('./routes/brand.$category.$brand'));
const VehiclePage = lazy(() => import('./routes/vehicle.$category.$brand.$model'));
const LoginPage = lazy(() => import('./routes/auth/login'));
const RegisterPage = lazy(() => import('./routes/auth/register'));
const GoogleCallbackPage = lazy(() => import('./routes/auth/google-callback'));

const router = createBrowserRouter([
  {
    path: '/welcome',
    element: <LandingPage />,
  },
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <FeedPage /> },
      { path: 'post/:id', element: <PostPage /> },
      // T-244: editorial articles are published at /article/<slug> (see
      // backend articleUrl()). This mount was missing entirely, so every
      // published article rendered a 404 error boundary at HTTP 200.
      { path: 'article/:id', element: <PostPage /> },
      { path: 'garage/:slug', element: <GaragePage /> },
      { path: 'garages', element: <GaragesPage /> },
      { path: 'garages/new', element: <CreateGaragePage /> },
      { path: 'garage/profile/:username', element: <GarageProfilePage /> },
      { path: 'user/:username', element: <UserPage /> },
      { path: 'user/:username/picks-2026', element: <UserPicks2026Page /> },
      { path: 'user/:username/followers', element: <UserFollowersPage /> },
      { path: 'user/:username/following', element: <UserFollowingPage /> },
      { path: 'bookmarks', element: <BookmarksPage /> },
      { path: 'invite', element: <InvitePage /> },
      { path: 'extension', element: <ExtensionWaitlistPage /> },
      // T-196: compliance/static pages. Public by construction — _layout is a deny-list
      // (PRIVATE_PREFIXES) and these are deliberately not on it.
      { path: 'privacy', element: <PrivacyPage /> },
      { path: 'terms', element: <TermsPage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'contact', element: <ContactPage /> },
      { path: 'history', element: <HistoryPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'leaderboard', element: <LeaderboardPage /> },
      { path: 'pro', element: <ProPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'compare', element: <AIComparePage /> },
      { path: 'search', element: <SearchPage /> },
      { path: 'explore', element: <ExplorePage /> },
      { path: 'sources', element: <SourcesPage /> },
      { path: 'source/:id', element: <SourcePage /> },
      { path: 'scooters', element: <ScootersPage /> },
      { path: 'launches', element: <LaunchesPage /> },
      { path: 'launches/upcoming', element: <LaunchCalendarPage /> },
      { path: 'events/indian-bike-week-2026', element: <IndianBikeWeek2026Page /> },
      { path: 'bikes/triumph-350', element: <Triumph350Page /> },
      { path: 'cars/toyota/urban-cruiser-ebella', element: <ToyotaEbellaPage /> },
      { path: 'cars/mercedes/cla-electric-2026', element: <MercedesClaElectricPage /> },
      { path: 'cars/nissan/tekton', element: <NissanTektonPage /> },
      { path: 'cars/tata/sierra-ev', element: <TataSierraEvPage /> },
      { path: 'cars/tata/sierra-ev/launch-live', element: <SierraEvLaunchLivePage /> },
      { path: 'guides/bs7-emission-norms-india-2026', element: <Bs7EmissionNormsPage /> },
      { path: 'guides/best-ev-under-20-lakh-india-2026', element: <BestEvUnder20LPage /> },
      { path: 'ev/india-ev-two-wheeler-market-share', element: <IndiaEvTwoWheelerMarketSharePage /> },
      { path: 'bikes/gst-350cc-impact', element: <Gst350ccImpactPage /> },
      { path: 'bikes/gst-calculator', element: <GstCalculatorPage /> },
      { path: 'guides/delhi-ev-policy-calculator', element: <DelhiEvPolicyCalculatorPage /> },
      { path: 'guides/motogp-india-2026', element: <MotoGpIndia2026Page /> },
      { path: 'guides/cafe3-price-impact-calculator', element: <Cafe3PriceImpactCalculatorPage /> },
      { path: 'guides/evitara-baas-calculator', element: <EVitaraBaaSCalculatorPage /> },
      { path: 'guides/ev-subsidy-countdown', element: <EvSubsidyCountdownPage /> },
      { path: 'tools/ev-subsidy-calculator', element: <EvSubsidyCalculatorPage /> },
      { path: 'tools/emi-calculator', element: <EmiCalculatorPage /> },
      { path: 'tools/ev-insurance-calculator', element: <EVInsuranceCalculatorPage /> },
      { path: 'cars/mg/majestor-vs-fortuner-2026', element: <MajestorVsFortunerPage /> },
      { path: 'bikes/bsa/scrambler-650-vs-royal-enfield-bear-650', element: <BsaScrambler650VsBear650Page /> },
      { path: 'data/india-ev-pv-fy2026', element: <IndiaEvPvFy2026Page /> },
      { path: 'cars/mercedes-benz/cla-electric', element: <MercedesBenzClaElectricPage /> },
      { path: 'data/india-ev-fy2026', element: <IndiaEvFy2026Page /> },
      { path: 'data/india-ev-annual-report-fy2026', element: <IndiaEvAnnualReportFy2026Page /> },
      { path: 'data/india-2w-fy2026', element: <India2wFy2026Page /> },
      { path: 'data/india-car-sales', element: <IndiaCarSalesPage /> },
      { path: 'data/india-car-fy2026-top10', element: <IndiaCarFY2026Top10Page /> },
      { path: 'bikes/royal-enfield/himalayan-750', element: <Himalayan750Page /> },
      { path: 'bikes/bsa/scrambler-650', element: <BsaScrambler650Page /> },
      { path: 'cars/toyota/ebella-vs-maruti-e-vitara', element: <EbellaVsEVitaraPage /> },
      { path: 'data/india-march-2026-two-wheeler-sales', element: <IndiaMarch2026TwoWheelerSalesPage /> },
      { path: 'guides/motogp-2026-standings', element: <MotoGp2026StandingsPage /> },
      { path: 'cars/mercedes-benz/cla-electric-india-launch', element: <MercedesClaElectricIndiaLaunchPage /> },
      { path: 'cars/nissan/tekton-india-launch', element: <NissanTektonIndiaLaunchPage /> },
      { path: 'data/india-car-sales-march-2026', element: <IndiaCarSalesMarch2026Page /> },
      { path: 'cars/toyota/ebella-variant-guide', element: <EbellaVariantGuidePage /> },
      { path: 'cars/mg/majestor-india-launch', element: <MgMajestorIndiaLaunchPage /> },
      { path: 'guides/best-scooters-under-1-lakh-2026', element: <BestScootersUnder1Lakh2026Page /> },
      { path: 'cars/toyota/ebella-india-launch', element: <EbellaIndiaLaunchPage /> },
      { path: 'guides/best-car-discounts-april-2026', element: <BestCarDiscountsApril2026Page /> },
      { path: 'guides/bharat-ncap-5-star-cars-2026', element: <BharatNcap5StarCars2026Page /> },
      { path: 'data/india-car-sales-fy2026-analysis', element: <IndiaCarSalesFy2026AnalysisPage /> },
      { path: 'guides/delhi-ev-policy-2026-tax-exemptions', element: <DelhiEvPolicy2026TaxExemptionsPage /> },
      { path: 'guides/gst-hike-350cc-bikes-india-2026', element: <GstHike350ccBikesPage /> },
      { path: 'bikes/bsa/scrambler-650-india-launch', element: <BsaScrambler650IndiaLaunchPage /> },
      { path: 'bikes/royal-enfield/himalayan-750-india-launch', element: <Himalayan750IndiaLaunchPage /> },
      { path: 'data/india-ev-q1-2026-sales', element: <IndiaEvQ1_2026SalesPage /> },
      { path: 'guides/beijing-auto-show-2026-india', element: <BeijingAutoShow2026IndiaPage /> },
      { path: 'guides/f1-miami-gp-2026-preview', element: <F1MiamiGp2026PreviewPage /> },
      { path: 'guides/chinese-evs-india-2026-buyers-guide', element: <ChineseEvsIndia2026BuyersGuidePage /> },
      { path: 'guides/f1-2026-season-guide', element: <F12026SeasonGuidePage /> },
      { path: 'guides/delhi-ev-policy-2026-2030', element: <DelhiEvPolicy20262030Page /> },
      { path: 'guides/kimi-antonelli-f1-2026-championship', element: <KimiAntonelliF12026ChampionshipPage /> },
      { path: 'data/global-ev-q1-2026-sales', element: <GlobalEvQ1_2026SalesPage /> },
      { path: 'data/india-pv-sales-march-2026', element: <IndiaPvSalesMarch2026Page /> },
      { path: 'guides/f1-2026-super-clipping-rule', element: <F12026SuperClippingRulePage /> },
      { path: 'guides/motogp-spanish-gp-jerez-2026', element: <MotoGpSpanishGpJerez2026Page /> },
      { path: 'guides/mg-majestor-buyers-guide-2026', element: <MgMajestorBuyersGuide2026Page /> },
      { path: 'guides/india-ev-charging-infrastructure-2026', element: <IndiaEvChargingInfrastructure2026Page /> },
      { path: 'guides/ev-charging-highways-india-2026', element: <EvChargingHighwaysIndia2026Page /> },
      { path: 'guides/f1-2026-monaco-gp-preview', element: <F1MonacoGp2026PreviewPage /> },
      { path: 'guides/india-2w-fy2026-sales-analysis', element: <India2wFy2026SalesAnalysisPage /> },
      { path: 'cars/toyota/ebella-baas-buyers-guide-2026', element: <EbellaBaasBuyersGuide2026Page /> },
      { path: 'guides/india-bike-launches-april-2026', element: <IndiaBikeLaunchesApril2026Page /> },
      { path: 'guides/wrc-2026-croatia-rally', element: <WrcCroatiaRally2026Page /> },
      { path: 'guides/motogp-2026-mid-season-analysis', element: <MotoGp2026MidSeasonAnalysisPage /> },
      { path: 'guides/delhi-ev-mandate-2026-2030', element: <DelhiEvMandate20262030Page /> },
      { path: 'guides/f1-2026-regulation-crisis', element: <F12026RegulationCrisisPage /> },
      { path: 'guides/april-23-triple-bike-launch-india-2026', element: <April23TripleBikeLaunchPage /> },
      { path: 'guides/flying-flea-c6-baas-vs-ownership', element: <FlyingFleaC6BaasVsOwnershipPage /> },
      // T-242: the data-driven vehicle page, mounted BELOW all 116 literal
      // vehicle routes on purpose. React Router matches a static segment
      // before a dynamic one, so every hand-written page still serves itself;
      // this is reachable only for a path with no literal route. Ruling 7 —
      // add, prove, then delete, never in one commit.
      { path: ':category/:brand/:model', element: <VehiclePage /> },
      { path: 'cars/:brand', element: <BrandHubPage /> },
      { path: 'bikes/:brand', element: <BrandHubPage /> },
      { path: 'find/:slug', element: <BudgetDiscoveryPage /> },
      { path: 'market/india-auto-sales', element: <IndiaAutoSalesPage /> },
      { path: 'digest', element: <DigestPage /> },
      { path: 'dispatch', element: <DispatchPage /> },
      { path: 'status', element: <StatusPage /> },
      { path: 'tags', element: <TagsPage /> },
      { path: 'tag/:slug', element: <TagPage /> },
      { path: 'runtime/ai-quality', element: <AiQualityDashboard /> },
      { path: 'runtime/share-breakdown', element: <ShareBreakdownDashboard /> },
      { path: 'runtime/affiliate-revenue', element: <AffiliateRevenueDashboard /> },
      { path: 'runtime/pro-funnel', element: <ProFunnelDashboard /> },
    ],
  },
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'google/callback', element: <GoogleCallbackPage /> },
    ],
  },
]);

// T-137: recover from a post-deploy stale chunk BEFORE anything else can throw
// one. Installed ahead of the SW registration and of render so a returning
// visitor's dead-chunk import is turned into one reload, not an error wall.
import { installStaleChunkRecovery } from './lib/stale-chunk';
installStaleChunkRecovery();

// GROWTH-003: Register service worker for PWA support
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      // T-137: a returning visitor can be controlled by an OLD sw.js whose
      // fetch handler is cache-first for navigations. Force an update check on
      // every load so the fixed worker takes over on the next visit rather
      // than whenever the browser next feels like checking.
      reg.update?.().catch(() => {});
    }).catch(() => {});
  });
}

// ANALYTICS-032: Track push notification permission state on app load
import { trackPushPermissionState } from './lib/analytics';
trackPushPermissionState(
  'Notification' in window ? Notification.permission : 'unsupported',
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* T-173: OUTSIDE Suspense on purpose. A lazy route that fails to import
          throws during render; the boundary must sit ABOVE the Suspense that
          is awaiting it, or the throw escapes past it to the router's
          dev-facing error wall. */}
      <StaleChunkBoundary>
        <Suspense fallback={<div className="flex h-screen items-center justify-center text-text-tertiary">Loading…</div>}>
          <RouterProvider router={router} />
        </Suspense>
      </StaleChunkBoundary>
    </QueryClientProvider>
  </React.StrictMode>
);
